<template>
    <div class="relative w-full mx-auto" id="transcript-tool">

        <!-- ── INITIAL HERO STATE (URL Input & Simple Forms) ── -->
        <div v-if="!transcript && !isProcessing" class="w-full">
            <div style="background:var(--dark2);border:1px solid var(--border);border-radius:16px;padding:10px;display:flex;gap:10px;margin-bottom:20px;max-width:680px;margin-left:auto;margin-right:auto" class="glow">
                <div style="display:flex;align-items:center;padding-left:8px">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" stroke-width="2">
                        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                    </svg>
                </div>
                <!-- Input -->
                <input class="url-input"
                    id="video-url-input"
                    v-model="videoUrl"
                    type="url"
                    placeholder="Paste any TikTok, YouTube, Reel, or X video link here..."
                    :disabled="isProcessing"
                    @keyup.enter="handleTranscribe"
                />

                <!-- Transcribe Button -->
                <button
                    id="generate-transcript-btn"
                    @click="handleTranscribe"
                    :disabled="!videoUrl.trim() || isProcessing"
                    class="btn-primary"
                    style="padding:12px 24px;font-size:15px;border-radius:10px;flex-shrink:0;cursor:pointer;font-weight:700;white-space:nowrap"
                >
                    <span>Transcribe Now →</span>
                </button>
            </div>

            <!-- Error Notification -->
            <div v-if="error" class="mt-4 max-w-xl mx-auto animate-in fade-in slide-in-from-top-2 duration-300">
                <div class="bg-red-500/10 border border-red-400/20 rounded-2xl p-4 flex items-start gap-3">
                    <svg class="w-5 h-5 text-red-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div class="flex-1">
                        <p class="text-red-300 font-semibold text-sm">Transcription Failed</p>
                        <p class="text-red-300/80 text-sm mt-0.5">{{ error }}</p>
                    </div>
                    <button @click="error = null" class="text-red-400/60 hover:text-red-300 transition-colors">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Simple Static History list on Homepage (if no active transcript) -->
            <div v-if="history.length > 0" class="max-w-[680px] mx-auto mt-12 bg-[#0d1320] border border-[#1e2d4a] rounded-2xl p-6">
                <h4 class="font-['Syne',sans-serif] font-bold text-white text-[15px] tracking-wide mb-4 flex items-center gap-2">
                    ⏱ Recent Workspaces
                </h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div v-for="item in history.slice(0, 4)" :key="item.id" 
                        class="bg-[#131b2e] border border-[#1e2d4a] rounded-xl p-3 flex gap-3 cursor-pointer transition-all duration-200 hover:border-[#00e5a0]/30 hover:bg-[#1a2540]"
                        @click="loadFromHistory(item)">
                        <div class="w-10 h-10 rounded-lg bg-[#00e5a0]/15 flex items-center justify-center font-bold text-[#00e5a0] text-sm shrink-0">
                            {{ item.platform === 'YouTube' ? '▶️' : item.platform === 'TikTok' ? '🎵' : '📸' }}
                        </div>
                        <div class="min-w-0">
                            <div class="text-white text-xs font-semibold truncate">{{ item.title }}</div>
                            <div class="text-[#5a7090] text-[10px] mt-1 uppercase font-bold tracking-wider">{{ item.platform }} · {{ item.wordCount }} words</div>
                        </div>
                    </div>
                </div>
                <div class="flex justify-between items-center mt-4 pt-3 border-t border-[#1e2d4a]">
                    <span class="text-xs text-[#5a7090]">You have {{ history.length }} workspaces saved in local storage.</span>
                    <button @click="clearHistory" class="text-xs font-bold text-red-400 hover:text-red-300">Clear all</button>
                </div>
            </div>
        </div>

        <!-- ── REALTIME IMMERSIVE LOADING SCREEN takeover ── -->
        <div v-if="isProcessing" class="fixed inset-0 bg-[#070d18] z-[9999] flex flex-col items-center justify-center gap-8 workspace-root font-sans" style="line-height:1.5; color:var(--text)">
            <div class="text-center">
                <!-- Branding -->
                <div class="flex items-center gap-2.5 justify-center mb-9 animate-pulse">
                    <div class="w-7 h-7 bg-[#00e5a0] rounded-lg flex items-center justify-center shadow-lg shadow-[#00e5a0]/30">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#07111e" stroke-width="3">
                            <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                        </svg>
                    </div>
                    <span class="text-xl font-bold text-white font-['Syne',sans-serif] tracking-tight">
                        todo<span class="text-[#00e5a0]">scribe</span>
                    </span>
                </div>

                <!-- Video Status Block -->
                <div class="bg-[#0b1221] border border-[#1a2d44] rounded-2xl p-5 w-[420px] max-w-full text-left mb-7 shadow-2xl">
                    <div class="flex items-center gap-2.5 mb-2.5">
                        <div class="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping"></div>
                        <span class="text-[11px] text-[#42607e] font-bold overflow-hidden text-ellipsis whitespace-nowrap block flex-1">
                            {{ videoUrl }}
                        </span>
                    </div>
                    <div class="font-bold text-white text-sm leading-snug">
                        Processing Transcripts & Content Formats...
                    </div>
                </div>

                <!-- Spinner -->
                <div class="spinner mb-6 mx-auto"></div>

                <!-- Messages -->
                <div class="text-[13px] text-[#42607e] min-h-[20px] font-semibold mb-6">
                    {{ loadingMessage }}
                </div>

                <!-- Progress Bar -->
                <div class="w-[360px] max-w-full mx-auto px-4 sm:px-0">
                    <div class="prog-track">
                        <div class="prog-fill" :style="`width: ${progress}%`"></div>
                    </div>
                    <div class="flex justify-between mt-2.5 text-[11px] text-[#42607e] font-bold uppercase tracking-wider">
                        <span>AI Repurposing Engine</span>
                        <span>{{ Math.round(progress) }}%</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── FULLSCREEN COMPREHENSIVE WORKSPACE TAKE-OVER ── -->
        <div v-if="transcript && !isProcessing" class="fixed inset-0 bg-[#070d18] z-[999] flex flex-col workspace-root font-sans overflow-hidden" style="line-height:1.5; color:var(--text)">
            
            <!-- ═ TOP NAVIGATION BAR ═ -->
            <nav id="main-nav" class="flex-shrink-0" style="background:rgba(7,13,24,.92); backdrop-filter:blur(20px); border-bottom:1px solid var(--border)">
                <div class="px-5 flex items-center justify-between h-[58px]">
                    
                    <!-- Brand / Title Path -->
                    <div class="flex items-center gap-4">
                        <a href="#" @click.prevent="reset" class="flex items-center gap-2.5 text-decoration-none">
                            <div class="w-6 h-6 bg-[#00e5a0] rounded flex items-center justify-center shadow-md shadow-[#00e5a0]/20">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#07111e" stroke-width="3">
                                    <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                                </svg>
                            </div>
                            <span class="text-[15px] font-bold text-white font-['Syne',sans-serif]">todo<span class="text-[#00e5a0]">scribe</span></span>
                        </a>
                        <span class="text-[#203650] select-none">/</span>
                        <span class="text-[12px] text-[#42607e] font-semibold">Workspace</span>
                        <span class="text-[#203650] select-none">/</span>
                        <span class="text-[12px] text-white font-bold max-w-[280px] truncate" :title="workspaceTitle">
                            {{ workspaceTitle }}
                        </span>
                    </div>

                    <!-- Action Bar -->
                    <div class="flex items-center gap-2">
                        <button class="btn-sm" @click="copyTabContent">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1 inline"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                            Share Workspace
                        </button>
                        <button class="btn-sm" @click="downloadTabTxt">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1 inline"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            Export Tab
                        </button>
                        <button class="btn-sm btn-green" @click="triggerToast('Pushed directly to connected Notion Page!')">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1 inline"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                            Sync Notion
                        </button>
                        <div class="w-[1px] h-7 bg-[#1a2d44] mx-2 select-none"></div>
                        
                        <!-- Avatar -->
                        <div class="w-[30px] h-[30px] border border-[#00e5a0]/20 rounded-full bg-[#00e5a0]/15 flex items-center justify-center text-[12px] font-bold text-[#00e5a0]">
                            US
                        </div>
                    </div>
                </div>
            </nav>

            <!-- ═ WORKSPACE MAIN SHELL ═ -->
            <div id="app-shell" class="flex flex-1 overflow-hidden">
                
                <!-- ── LEFT SIDEBAR ── -->
                <aside id="left-sidebar">
                    
                    <!-- Sidebar Header Info -->
                    <div class="sb-header">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-1.5">
                                <div class="w-[22px] height-[22px] bg-[#00e5a0]/10 border border-[#00e5a0]/20 rounded-md flex items-center justify-center">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#00e5a0" stroke-width="2.5">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                    </svg>
                                </div>
                                <span class="text-[12.5px] font-bold text-white font-['Syne',sans-serif]">AI Content Studio</span>
                            </div>
                            <span class="text-[9px] bg-[#00e5a0]/10 text-[#00e5a0] px-2 py-0.5 rounded-full border border-[#00e5a0]/20 font-extrabold tracking-wide uppercase">PRO</span>
                        </div>
                    </div>

                    <!-- Sidebar Body Scroll -->
                    <div class="sb-body">
                        
                        <!-- Section 1: Generators -->
                        <div class="sb-section">
                            <div class="sb-section-title">
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="mr-1.5 inline"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                                Generate content
                            </div>
                            <div class="gen-grid">
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'linkedin' }" @click="activeGen = 'linkedin'">
                                    <span class="gicon">💼</span>LinkedIn Post
                                </button>
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'article' }" @click="activeGen = 'article'">
                                    <span class="gicon">✍️</span>Blog Article
                                </button>
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'hooks' }" @click="activeGen = 'hooks'">
                                    <span class="gicon">🎣</span>Hook Lines
                                </button>
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'thread' }" @click="activeGen = 'thread'">
                                    <span class="gicon">🐦</span>Tweet Thread
                                </button>
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'newsletter' }" @click="activeGen = 'newsletter'">
                                    <span class="gicon">📧</span>Newsletter
                                </button>
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'caption' }" @click="activeGen = 'caption'">
                                    <span class="gicon">📸</span>IG Caption
                                </button>
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'subjects' }" @click="activeGen = 'subjects'">
                                    <span class="gicon">📬</span>Email Subject
                                </button>
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'ytdesc' }" @click="activeGen = 'ytdesc'">
                                    <span class="gicon">▶️</span>YT Description
                                </button>
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'seo' }" @click="activeGen = 'seo'">
                                    <span class="gicon">🔍</span>SEO Meta
                                </button>
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'titles' }" @click="activeGen = 'titles'">
                                    <span class="gicon">🏷️</span>Video Titles
                                </button>
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'podcast' }" @click="activeGen = 'podcast'">
                                    <span class="gicon">🎙️</span>Show Notes
                                </button>
                                <button class="gen-btn" :class="{ 'active-gen': activeGen === 'outreach' }" @click="activeGen = 'outreach'">
                                    <span class="gicon">🤝</span>Cold Outreach
                                </button>
                            </div>
                        </div>

                        <!-- Section 2: Tone Selection -->
                        <div class="sb-section">
                            <div class="sb-section-title">
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="mr-1.5 inline"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                                Tone & Style
                            </div>
                            <div class="tone-wrap">
                                <span v-for="t in ['Professional', 'Casual', 'Viral', 'Educational', 'Witty', 'Persuasive', 'Storytelling', 'Bold']" 
                                    :key="t" class="tone-pill" :class="{ 'active': activeTone === t }" @click="activeTone = t">
                                    {{ t }}
                                </span>
                            </div>
                        </div>

                        <!-- Section 3: Length slider -->
                        <div class="sb-section">
                            <div class="sb-section-title">
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="mr-1.5 inline"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                                Content Length
                            </div>
                            <div class="len-opt">
                                <span v-for="l in ['Short', 'Medium', 'Long']" :key="l" 
                                    class="len-btn" :class="{ 'active': activeLength === l }" @click="activeLength = l">
                                    {{ l }}
                                </span>
                            </div>
                        </div>

                        <!-- Section 4: Language output selection -->
                        <div class="sb-section">
                            <div class="sb-section-title">
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="mr-1.5 inline"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                                Output Language
                            </div>
                            <select v-model="outputLang" @change="triggerToast('Language output set to ' + outputLang)" class="w-full bg-[#101926] border border-[#1a2d44] rounded-lg p-2 text-xs font-semibold text-[#9ab8d8] outline-none cursor-pointer">
                                <option>English</option>
                                <option>Urdu</option>
                                <option>Arabic</option>
                                <option>Spanish</option>
                                <option>French</option>
                                <option>German</option>
                                <option>Hindi</option>
                                <option>Bengali</option>
                                <option>Japanese</option>
                                <option>Korean</option>
                            </select>
                        </div>

                        <!-- Section 5: Custom Prompt -->
                        <div class="sb-section">
                            <div class="sb-section-title">
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="mr-1.5 inline"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                                Custom prompt instruction
                            </div>
                            <textarea v-model="customPrompt" class="prompt-area" rows="3" placeholder="e.g. Write a viral hook for a 25-year-old entrepreneur who struggles with consistency..."></textarea>
                            <div class="flex gap-2 mt-2">
                                <button class="btn-sm flex-1 justify-center text-[10.5px] py-1.5" @click="customPrompt = ''">Clear</button>
                                <button class="btn-sm flex-[2] justify-center text-[10.5px] py-1.5 border-[#00e5a0]/20 text-[#00e5a0] hover:bg-[#00e5a0]/5" @click="customPrompt = 'Write a viral hook for a 25-year-old entrepreneur struggling with content consistency — make it punchy and relatable.'">Use Example</button>
                            </div>
                        </div>

                        <!-- Generator Submit Button -->
                        <button class="generate-btn font-['Syne',sans-serif]" :disabled="isGenerating" @click="runGenerate">
                            <svg v-if="!isGenerating" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="mr-1 inline"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                            <svg v-else class="ai-pulse inline mr-1" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                            {{ isGenerating ? 'Generating...' : 'Generate Now' }}
                        </button>

                        <!-- Generated Content block -->
                        <div v-if="generatedOutputText" class="mt-4 bg-[#101926] border border-[#00e5a0]/20 rounded-xl p-3.5 shadow-lg animate-in fade-in zoom-in-95 duration-300">
                            <div class="flex items-center justify-between mb-2">
                                <span class="text-[10px] font-extrabold text-[#00e5a0] tracking-widest uppercase">{{ generatedOutputLabel }}</span>
                                <button class="text-[10px] font-bold bg-[#162030] border border-[#1a2d44] rounded px-2.5 py-1 text-[#6b90b0] hover:text-[#00e5a0] hover:border-[#00e5a0]/30 transition-colors" @click="copyGenerated">Copy</button>
                            </div>
                            <div class="text-[12.5px] text-[#9ab8d8] leading-relaxed whitespace-pre-line font-medium max-h-[220px] overflow-y-auto pr-1">
                                {{ generatedOutputText }}
                            </div>
                        </div>

                        <!-- Spacer line -->
                        <div class="h-[1px] bg-[#1a2d44] my-4 select-none"></div>

                        <!-- Export Integration Options -->
                        <div class="sb-section">
                            <div class="sb-section-title">
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="mr-1.5 inline"><polyline points="21 15 16 10 11 15"/><line x1="16" y1="10" x2="16" y2="21"/></svg>
                                Export Integration
                            </div>
                            <div class="export-row" @click="triggerToast('Pushed directly to WordPress!')">
                                <div class="exp-name"><div class="exp-logo bg-[#21759b]">🌐</div>WordPress</div>
                                <span class="exp-arrow">→</span>
                            </div>
                            <div class="export-row" @click="triggerToast('Pushed directly to Mailchimp!')">
                                <div class="exp-name"><div class="exp-logo bg-[#ffe01b]">🐵</div>Mailchimp</div>
                                <span class="exp-arrow">→</span>
                            </div>
                            <div class="export-row" @click="triggerToast('Pushed directly to ConvertKit!')">
                                <div class="exp-name"><div class="exp-logo bg-[#fb6970]">✉️</div>ConvertKit</div>
                                <span class="exp-arrow">→</span>
                            </div>
                            <div class="export-row" @click="triggerToast('Downloaded Subtitles (.SRT)!')">
                                <div class="exp-name"><div class="exp-logo bg-[#162030] text-xs">📃</div>.SRT Captions</div>
                                <span class="exp-arrow">→</span>
                            </div>
                        </div>

                        <!-- Sidebar Usage Stats Box -->
                        <div class="sb-section">
                            <div class="sb-section-title">⚡ Limits & Quota</div>
                            <div class="bg-[#101926] border border-[#1a2d44] rounded-xl p-3">
                                <div class="flex justify-between items-center mb-1.5">
                                    <span class="text-[11.5px] text-[#6b90b0]">Monthly Usage</span>
                                    <span class="text-[11.5px] text-white font-extrabold">{{ history.length }} / 5</span>
                                </div>
                                <div class="usage-bar">
                                    <div class="usage-fill" :style="`width: ${Math.min(history.length * 20, 100)}%`"></div>
                                </div>
                                <div class="text-[10px] text-[#42607e] mt-1.5 font-semibold">
                                    {{ Math.max(5 - history.length, 0) }} free spaces remaining this month
                                </div>
                            </div>
                        </div>

                        <!-- Sidebar Workspaces History List -->
                        <div class="sb-section">
                            <div class="sb-section-title">⏱ Recent Workspaces</div>
                            <div v-for="item in history" :key="item.id" class="hist-item" :class="{ 'active-gen': item.url === videoUrl }" @click="loadFromHistory(item)">
                                <div class="hist-thumb" :style="`background: ${item.thumbBg || 'var(--dark4)'}`">▶️</div>
                                <div class="min-w-0">
                                    <div class="hist-title truncate">{{ item.title }}</div>
                                    <div class="hist-meta font-bold uppercase tracking-wider text-[9px]">{{ item.platform }} · {{ item.wordCount }} words</div>
                                </div>
                            </div>
                            <div v-if="history.length === 0" class="text-xs text-[#42607e] text-center py-4">
                                No saved history.
                            </div>
                        </div>

                    </div>
                </aside>

                <!-- ── MAIN CONTENT AREA ── -->
                <main id="main-area">

                    <!-- REAL TIME DYNAMIC VIDEO CARD -->
                    <div class="video-card">
                        <!-- Thumbnail -->
                        <div class="video-thumb" style="overflow:hidden;border-radius:4px;flex-shrink:0;width:200px;height:112px;background:var(--dark4);position:relative;">
                            <img
                                v-if="formatsData && formatsData.thumbnail"
                                :src="formatsData.thumbnail"
                                alt="Video Thumbnail"
                                style="width:100%;height:100%;object-fit:cover;"
                                @error="$event.target.style.display='none'"
                            />
                            <a v-if="videoUrl" :href="videoUrl" target="_blank" class="video-thumb-link-overlay">
                                <div class="play-over">
                                    <div class="yt-play-btn">
                                        <svg viewBox="0 0 68 48" width="44" height="31" class="yt-play-svg">
                                            <path d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,0.13,34,0,34,0S12.21,0.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z" class="yt-play-bg"></path>
                                            <polygon points="27,16 27,32 41,24" fill="#fff"></polygon>
                                        </svg>
                                    </div>
                                    <div class="yt-watch-bar">
                                        <span>{{ watchPlatformText }}</span>
                                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="inline-block ml-0.5 opacity-80">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <line x1="10" y1="14" x2="21" y2="3"></line>
                                        </svg>
                                    </div>
                                </div>
                            </a>
                        </div>
                        <!-- Info -->
                        <div class="flex-1 min-w-0">
                            <div class="flex items-start justify-between gap-3 mb-2.5">
                                <h2 class="text-xl font-bold mt-1 text-white leading-snug font-['Syne',sans-serif]">
                                    {{ formatsData ? formatsData.videoTitle : 'Video Transcription Title' }}
                                </h2>
                                <span class="bg-[#00e5a0]/10 text-[#00e5a0] text-[9.5px] px-2.5 py-1 rounded-full border border-[#00e5a0]/20 font-extrabold uppercase tracking-wide whitespace-nowrap">✓ Processed</span>
                            </div>
                            <div class="flex gap-2 flex-wrap select-none mt-5">
                                <span class="meta-chip">
                                    <Video :size="14" class="text-[#00e5a0]" />
                                    <span>{{ formatsData ? formatsData.platform : 'Video' }}</span>
                                </span>
                                <span class="meta-chip">
                                    <Clock :size="14" class="text-[#00e5a0]" />
                                    <span>{{ formatsData && formatsData.duration ? formatsData.duration : (wordCount ? Math.round(wordCount / 140) + 'm' : '--') }}</span>
                                </span>
                                <span class="meta-chip" v-if="formatsData && formatsData.uploader">
                                    <User :size="14" class="text-[#00e5a0]" />
                                    <span>{{ formatsData.uploader }}</span>
                                </span>
                                <span class="meta-chip" v-if="formatsData && formatsData.views">
                                    <Eye :size="14" class="text-[#00e5a0]" />
                                    <span>{{ formatsData.views }}</span>
                                </span>
                                <span class="meta-chip overflow-hidden" style="padding: 0; gap: 0;" v-if="formatsData && formatsData.likes && formatsData.likes !== '--'">
                                    <span class="flex items-center gap-1.5 px-3 py-1.5 hover:bg-white/5 transition-colors cursor-pointer" title="Likes">
                                        <ThumbsUp :size="14" class="text-[#00e5a0]" />
                                        <span>{{ formatsData.likes }}</span>
                                    </span>
                                    <span class="w-[1px] h-4 bg-[#1a2d44] self-center"></span>
                                    <span class="flex items-center gap-1.5 px-3 py-1.5 hover:bg-white/5 transition-colors cursor-pointer" title="Dislikes">
                                        <ThumbsDown :size="14" class="text-[#00e5a0]" />
                                        <span>{{ formatsData.dislikes }}</span>
                                    </span>
                                </span>
                                <span class="meta-chip" v-if="formatsData && formatsData.upload_date">
                                    <Calendar :size="14" class="text-[#00e5a0]" />
                                    <span>{{ formatsData.upload_date }}</span>
                                </span>
                                <span class="meta-chip">
                                    <Globe :size="14" class="text-[#00e5a0]" />
                                    <span>{{ detectedLanguage || 'English' }}</span>
                                </span>
                            </div>
                        </div>
                        <!-- Header tool options -->
                        <div class="flex gap-1.5 shrink-0">
                            <button class="btn-sm" @click="copyVideoLink" title="Copy Source Link">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                            </button>
                            <button class="btn-sm" @click="handleTranscribe" title="Reprocess Transcript">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
                            </button>
                        </div>
                    </div>

                    <!-- WORKSPACE TAB NAVIGATION & TAB BAR TOOLBAR -->
                    <div class="flex items-center justify-between gap-3 mb-3 overflow-x-auto pb-1">
                        <div class="tab-wrap" id="tabs">
                            <button class="tab-btn" :class="{ 'active': activeTab === 'transcript' }" @click="activeTab = 'transcript'">📝 Transcript</button>
                            <button class="tab-btn" :class="{ 'active': activeTab === 'tags' }" @click="activeTab = 'tags'">🏷️ Tags</button>
                            <button class="tab-btn" :class="{ 'active': activeTab === 'ytdesc' }" @click="activeTab = 'ytdesc'">🎬 YT Description</button>
                        </div>
                        
                        <!-- Mini toolbar controls -->
                        <div class="flex gap-2 items-center shrink-0">
                            <!-- Search box inside transcript tab -->
                            <input v-if="activeTab === 'transcript'" v-model="searchText" type="text" placeholder="Search lines..." class="bg-[#101926] border border-[#1a2d44] rounded-lg px-3 py-1.5 text-xs text-[#9ab8d8] outline-none w-[160px] focus:border-[#00e5a0] transition-colors" />
                            <button class="btn-sm text-xs" @click="copyTabContent">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1 inline"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                                Copy Tab
                            </button>
                            <button class="btn-sm text-xs" @click="downloadTabTxt">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="inline"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            </button>
                        </div>
                    </div>

                    <!-- ACTIVE TABS WORKSPACE MAIN PANELS -->
                    <div class="panel-wrap">
                        
                        <!-- TAB 1: INTERACTIVE TRANSCRIPT -->
                        <div class="panel" :class="{ 'active': activeTab === 'transcript' }">
                            <div class="panel-topbar">
                                <div class="flex gap-2 items-center">
                                    <span class="text-[12px] font-bold text-white uppercase tracking-wider">Full Verbatim Transcript</span>
                                    <span class="bg-[#00e5a0]/10 text-[#00e5a0] text-[9.5px] px-2 py-0.5 rounded-full border border-[#00e5a0]/20 font-bold">2 Speakers</span>
                                </div>
                                <div class="flex gap-4 items-center">
                                    <label class="flex items-center gap-1.5 text-[11px] text-[#42607e] font-semibold cursor-pointer"><input type="checkbox" v-model="showTimestamps" class="accent-[#00e5a0]"/>Timestamps</label>
                                    <label class="flex items-center gap-1.5 text-[11px] text-[#42607e] font-semibold cursor-pointer"><input type="checkbox" v-model="showSpeakers" class="accent-[#00e5a0]"/>Speakers</label>
                                </div>
                            </div>
                            <div class="panel-body flex flex-col gap-0.5">
                                <div v-for="(line, idx) in filteredTranscriptLines" :key="idx" class="tr-line">
                                    <div class="ts-stamp font-mono" :style="`visibility: ${showTimestamps ? 'visible' : 'hidden'}`">{{ line.ts }}</div>
                                    <div class="min-w-0 flex-1">
                                        <div v-if="showSpeakers" class="spk font-extrabold text-[9px] text-[#42607e] uppercase tracking-wider mb-0.5">{{ line.sp }}</div>
                                        <div class="tr-text text-[#9ab8d8] text-[13.5px] leading-relaxed">{{ line.text }}</div>
                                    </div>
                                </div>
                                <div v-if="filteredTranscriptLines.length === 0" class="text-center text-[#42607e] py-8 text-sm">
                                    No segments found matching "{{ searchText }}"
                                </div>
                            </div>
                        </div>



                        <!-- TAB 6: YOUTUBE DESCRIPTION -->
                        <div class="panel" :class="{ 'active': activeTab === 'ytdesc' }">
                            <div class="panel-topbar">
                                <div class="flex gap-2 items-center">
                                    <span class="text-[12px] font-bold text-white uppercase tracking-wider">Video Description</span>
                                    <span v-if="formatsData && formatsData.description" class="bg-[#00e5a0]/10 text-[#00e5a0] text-[9.5px] px-2 py-0.5 rounded-full border border-[#00e5a0]/20 font-bold">{{ formatsData.description.length }} chars</span>
                                </div>
                                <button class="btn-sm text-[11px] py-1 px-2.5" @click="copyYtDesc" :disabled="!formatsData || !formatsData.description">Copy Description</button>
                            </div>
                            <div class="panel-body flex flex-col gap-4" v-if="formatsData">
                                <div class="sum-box">
                                    <div class="text-[10px] font-extrabold text-[#42607e] uppercase tracking-wider mb-3 flex items-center gap-1.5 select-none">
                                        <FileText :size="10" class="text-[#42607e]" /> YouTube Video Description
                                    </div>
                                    <div v-if="formatsData.description" class="text-[13.5px] text-[#9ab8d8] leading-relaxed select-text whitespace-pre-wrap max-h-[360px] overflow-y-auto pr-1">
                                        {{ formatsData.description }}
                                    </div>
                                    <div v-else class="text-sm text-[#42607e] italic py-4">
                                        No description available for this video.
                                    </div>
                                </div>
                            </div>
                        </div>



                        <!-- TAB 8: VIDEO TAGS -->
                        <div class="panel" :class="{ 'active': activeTab === 'tags' }">
                            <div class="panel-topbar">
                                <span class="text-[12px] font-bold text-white uppercase tracking-wider">Video Metadata Tags</span>
                                <button class="btn-sm text-[11px] py-1 px-2.5" @click="copyTags" :disabled="!formatsData || !formatsData.tags || formatsData.tags.length === 0">Copy Tags</button>
                            </div>
                            <div class="panel-body flex flex-col gap-4" v-if="formatsData">
                                <div class="sum-box">
                                    <div class="text-[10px] font-extrabold text-[#42607e] uppercase tracking-wider mb-3 flex items-center gap-1.5 select-none">
                                        <Tag :size="10" class="text-[#42607e]" /> Associated Video Tags ({{ formatsData.tags.length }})
                                    </div>
                                    <div class="flex gap-2 flex-wrap select-none mt-2.5" v-if="formatsData.tags.length > 0">
                                        <span v-for="tag in formatsData.tags" :key="tag" class="text-[12px] bg-[#101926] hover:bg-[#162030] text-[#9ab8d8] hover:text-[#00e5a0] px-3.5 py-1.5 rounded-lg border border-[#1a2d44] transition-all cursor-pointer font-medium">
                                            #{{ tag }}
                                        </span>
                                    </div>
                                    <div v-else class="text-sm text-[#42607e] italic py-4">
                                        No tags found for this video.
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div><!-- end panel-wrap -->

                    <!-- TRANSCRIBE ANOTHER VIDEO BOTTOM BAR -->
                    <div class="mt-4 bg-[#0b1221] border border-[#1a2d44] rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4 select-none">
                        <div class="flex items-center gap-3">
                            <div class="w-8 h-8 bg-[#00e5a0]/10 border border-[#00e5a0]/20 rounded-lg flex items-center justify-center">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00e5a0" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            </div>
                            <div>
                                <div class="text-[13px] font-bold text-white">Transcribe another video</div>
                                <div class="text-[10px] text-[#42607e]">All formats will automatically sync dynamically</div>
                            </div>
                        </div>
                        <div class="flex gap-2 w-full sm:w-auto flex-1 max-w-md">
                            <input type="url" v-model="videoUrl" placeholder="Paste next video URL..." class="bg-[#101926] border border-[#1a2d44] rounded-lg px-3 py-2 text-xs text-[#9ab8d8] outline-none flex-1 focus:border-[#00e5a0] transition-colors" />
                            <button class="btn-sm btn-green" :disabled="!videoUrl.trim()" @click="handleTranscribe">Transcribe →</button>
                        </div>
                    </div>

                </main>
            </div>
        </div>

        <!-- ═ CLIENT TOAST SYSTEM NOTIFICATION ═ -->
        <div id="toast" :class="{ 'show': showToast }">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            <span id="toast-msg">{{ toastMessage }}</span>
        </div>

    </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import axios from 'axios';
import { Video, Clock, User, Eye, Calendar, Globe, ThumbsUp, ThumbsDown, Tag, FileText } from 'lucide-vue-next';

export default {
    name: 'VideoTranscriptTool',
    components: { Video, Clock, User, Eye, Calendar, Globe, ThumbsUp, ThumbsDown, Tag, FileText },

    setup() {
        // ── State ──────────────────────────────────────────────────────────
        const videoUrl         = ref('');
        const isProcessing     = ref(false);
        const transcript       = ref('');
        const detectedLanguage  = ref('');
        const error            = ref(null);
        const copied           = ref(false);
        const progress         = ref(0);
        const progressLabel    = ref('Processing');
        const loadingMessage   = ref('Connecting to platform...');
        const metaData         = ref(null);

        // Expanded UI states
        const activeTab        = ref('transcript');
        const activeGen        = ref('linkedin');
        const activeTone       = ref('Professional');
        const activeLength     = ref('Medium');
        const outputLang       = ref('English');
        const customPrompt     = ref('');
        const generatedOutputText  = ref('');
        const generatedOutputLabel = ref('LinkedIn Post');
        const isGenerating     = ref(false);
        const showToast        = ref(false);
        const toastMessage     = ref('Done!');
        const history          = ref([]);
        const showTimestamps   = ref(true);
        const showSpeakers     = ref(true);
        const searchText       = ref('');

        let stepInterval = null;
        let stepIndex = 0;

        // ── Helper: Format generator ──────────────────────────────────────
        const formatsData = computed(() => {
            if (!transcript.value) return null;
            
            const text = transcript.value.trim();
            const words = text.split(/\s+/).filter(Boolean);
            const wordCountVal = words.length;
            
            // Extract Platform & video title dynamically
            let platform = 'YouTube';
            let title = 'Video Transcription Title';
            let thumbnail = '';
            let duration = '15:00';
            let uploader = 'YouTube Channel';
            let upload_date = 'Recent';
            let views = 'Auto';
            let likes = '--';
            let dislikes = '--';
            let tags = [];
            let description = '';
            
            if (videoUrl.value.includes('tiktok.com')) {
                platform = 'TikTok';
                title = 'TikTok Creator Framework';
            } else if (videoUrl.value.includes('instagram.com')) {
                platform = 'Instagram';
                title = 'Viral Reel Strategy breakdown';
            } else if (videoUrl.value.includes('twitter.com') || videoUrl.value.includes('x.com')) {
                platform = 'X / Twitter';
                title = 'Viral X Video analysis';
            } else {
                // Try to make a friendly title from URL
                try {
                    const parsed = new URL(videoUrl.value);
                    if (parsed.searchParams.get('v')) {
                        title = `YouTube Video Workspace (${parsed.searchParams.get('v')})`;
                    } else {
                        title = `Video Transcription Workspace`;
                    }
                } catch {
                    title = 'Custom Video Workspace';
                }
            }

            if (metaData.value) {
                title = metaData.value.title || title;
                thumbnail = metaData.value.thumbnail || thumbnail;
                duration = metaData.value.duration || duration;
                uploader = metaData.value.uploader || uploader;
                upload_date = metaData.value.upload_date || upload_date;
                views = metaData.value.views || views;
                likes = metaData.value.likes || likes;
                dislikes = metaData.value.dislikes || dislikes;
                tags = metaData.value.tags || tags;
                description = metaData.value.description || description;
            }

            const sentences = text.split(/(?<=[.!?])\s+/).filter(Boolean);
            const tldr = sentences.slice(0, 3).join(' ') || 'Content repurposing system breakdown.';
            const takeaways = sentences.filter(s => s.length > 25).slice(1, 5);
            if (takeaways.length === 0) {
                takeaways.push('Film one exceptional video weekly. Quality at creation, quantity via distribution.');
                takeaways.push('Your transcript is the foundation. Every piece of written content originates from the transcript.');
                takeaways.push('Consistency over virality builds massive leverage channels.');
            }

            // Blog paragraphs
            const intro = sentences.slice(0, 4).join(' ') || 'Content systems represent the ultimate creator roadmap.';
            const s1 = sentences.slice(4, 9).join(' ') || 'Leverage your time by creating once and distributing everywhere.';
            const s2 = sentences.slice(9, 14).join(' ') || 'Building consistency is the key to scaling organic channels.';
            const conclusion = sentences.slice(14, 18).join(' ') || 'Set up automated workflows to streamline production budgets.';

            // Tweets
            const tweets = [];
            const segments = [
                `I just finished transcribing this viral video using AI. Steal the key insights below 👇 🧵`,
                sentences.slice(0, 2).join(' ').substring(0, 250),
                sentences.slice(2, 4).join(' ').substring(0, 250),
                sentences.slice(4, 6).join(' ').substring(0, 250),
                `The main lesson: Consistency beats virality. Work 10x smarter by building a content engine!`,
                `Transcribed instantly using TodoScribe. Highly recommend checking it out!`
            ].filter(Boolean);
            segments.forEach((s, idx) => {
                tweets.push({ n: idx + 1, text: s });
            });

            // LinkedIn Post
            const linkedin = `🎥 Just watched a fascinating video breakdown about content leverage.\n\nHere are the top takeaways you should steal:\n\n💡 ${sentences.slice(0, 2).join(' ') || 'Systems represent the ultimate creator roadmaps.'}\n\n🔥 ${sentences.slice(2, 4).join(' ') || 'Build a content engine to save hours of production work weekly.'}\n\n📈 ${sentences.slice(4, 6).join(' ') || 'Distribute your assets dynamically across platforms.'}\n\nWhat are your thoughts on this system? Let me know below!\n\n#ContentCreator #Systems #Leverage #Growth`;

            // Newsletter
            const subject = `Steal this video breakdown: How to build leverage 🚀`;
            const body = `Hey reader,\n\nAre you still filming seven different videos a week? If so, you're working much harder than you need to.\n\nHere's the exact content engine system that top creators use to build massive distribution with a fraction of the work.\n\nTL;DR:\n"${tldr}"\n\nKey Insights:\n• ${takeaways.join('\n• ')}\n\nI hope this helps you build consistency in your content workflows!\n\nBest,\nYour Content Copilot`;

            // YT Description
            const ytDesc = `🚀 Video transcription and content repurposing breakdown.\n\nIn this video, we unpack key insights extracted directly from the video track.\n\n📌 What you'll learn:\n• ${takeaways.slice(0, 2).join('\n• ')}\n\nChapters:\n0:00 Intro\n1:15 Core Topic\n3:42 Key Insights\n6:10 Repurposing Workflow\n8:20 Action Plan`;

            return {
                videoTitle: title,
                platform,
                thumbnail,
                duration,
                uploader,
                upload_date,
                views,
                likes,
                dislikes,
                tags,
                description,
                tldr,
                takeaways,
                blog: { intro, s1, s2, conclusion },
                tweets,
                linkedin,
                newsletter: { subject, body },
                ytDesc
            };
        });

        // ── Computed ───────────────────────────────────────────────────────
        const wordCount = computed(() =>
            transcript.value ? transcript.value.trim().split(/\s+/).filter(Boolean).length : 0
        );

        const workspaceTitle = computed(() => 
            formatsData.value ? formatsData.value.videoTitle : 'Transcription Workspace'
        );

        // Verbatim transcript paragraphs & speakers mapping
        const transcriptLines = computed(() => {
            if (!transcript.value) return [];
            
            const paragraphs = transcript.value.split(/\n+/).filter(Boolean);
            if (paragraphs.length === 0) return [];
            
            return paragraphs.map((p, idx) => {
                const minutes = Math.floor((idx * 35) / 60);
                const seconds = (idx * 35) % 60;
                const ts = `${minutes}:${seconds.toString().padStart(2, '0')}`;
                const sp = idx % 2 === 0 ? 'SPEAKER A' : 'SPEAKER B';
                return { ts, sp, text: p };
            });
        });

        // Filtered transcript lines for search toolbar
        const filteredTranscriptLines = computed(() => {
            const query = searchText.value.trim().toLowerCase();
            if (!query) return transcriptLines.value;
            return transcriptLines.value.filter(l => l.text.toLowerCase().includes(query));
        });

        const watchPlatformText = computed(() => {
            if (!formatsData.value) return 'Watch on YouTube';
            const p = formatsData.value.platform;
            if (p === 'X / Twitter') return 'Watch on X';
            return `Watch on ${p}`;
        });

        // ── Actions & Toast ────────────────────────────────────────────────
        const triggerToast = (msg) => {
            toastMessage.value = msg;
            showToast.value = true;
            setTimeout(() => {
                showToast.value = false;
            }, 2400);
        };

        const copyTabContent = async () => {
            let copyText = '';
            if (activeTab.value === 'transcript') {
                copyText = transcript.value;
            } else if (activeTab.value === 'blog' && formatsData.value) {
                copyText = `${formatsData.value.videoTitle}\n\n${formatsData.value.blog.intro}\n\n${formatsData.value.blog.s1}\n\n${formatsData.value.blog.s2}\n\n${formatsData.value.blog.conclusion}`;
            } else if (activeTab.value === 'tweets' && formatsData.value) {
                copyText = formatsData.value.tweets.map(t => `Tweet ${t.n}:\n${t.text}`).join('\n\n');
            } else if (activeTab.value === 'newsletter' && formatsData.value) {
                copyText = `Subject: ${formatsData.value.newsletter.subject}\n\n${formatsData.value.newsletter.body}`;
            } else if (activeTab.value === 'linkedin' && formatsData.value) {
                copyText = formatsData.value.linkedin;
            } else if (activeTab.value === 'ytdesc' && formatsData.value) {
                copyText = formatsData.value.description || formatsData.value.ytDesc;
            } else if (activeTab.value === 'summary' && formatsData.value) {
                copyText = `TL;DR:\n${formatsData.value.tldr}\n\nTakeaways:\n• ${formatsData.value.takeaways.join('\n• ')}`;
            } else if (activeTab.value === 'tags' && formatsData.value) {
                copyText = formatsData.value.tags.join(', ');
            }

            if (!copyText) return;
            try {
                await navigator.clipboard.writeText(copyText);
                triggerToast('Copied tab contents to clipboard!');
            } catch {
                triggerToast('Failed to copy. Please select manually.');
            }
        };

        const downloadTabTxt = () => {
            let fileContent = '';
            let filename = `todoscribe_${activeTab.value}_${Date.now()}.txt`;
            
            if (activeTab.value === 'transcript') {
                fileContent = transcript.value;
            } else if (activeTab.value === 'blog' && formatsData.value) {
                fileContent = `${formatsData.value.videoTitle}\n\n${formatsData.value.blog.intro}\n\n${formatsData.value.blog.s1}\n\n${formatsData.value.blog.s2}\n\n${formatsData.value.blog.conclusion}`;
            } else if (activeTab.value === 'tweets' && formatsData.value) {
                fileContent = formatsData.value.tweets.map(t => `Tweet ${t.n}:\n${t.text}`).join('\n\n');
            } else if (activeTab.value === 'newsletter' && formatsData.value) {
                fileContent = `Subject: ${formatsData.value.newsletter.subject}\n\n${formatsData.value.newsletter.body}`;
            } else if (activeTab.value === 'linkedin' && formatsData.value) {
                fileContent = formatsData.value.linkedin;
            } else if (activeTab.value === 'ytdesc' && formatsData.value) {
                fileContent = formatsData.value.description || formatsData.value.ytDesc;
            } else if (activeTab.value === 'summary' && formatsData.value) {
                fileContent = `TL;DR:\n${formatsData.value.tldr}\n\nTakeaways:\n• ${formatsData.value.takeaways.join('\n• ')}`;
            } else if (activeTab.value === 'tags' && formatsData.value) {
                fileContent = formatsData.value.tags.join(', ');
            }

            if (!fileContent) return;
            const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            triggerToast('File downloaded successfully!');
        };

        const copyVideoLink = async () => {
            try {
                await navigator.clipboard.writeText(videoUrl.value);
                triggerToast('Copied video link to clipboard!');
            } catch {
                triggerToast('Failed to copy URL.');
            }
        };

        // Tweet thread sub-items copy
        const copySingleTweet = async (tweet) => {
            try {
                await navigator.clipboard.writeText(tweet.text);
                triggerToast(`Tweet ${tweet.n} copied!`);
            } catch {
                triggerToast('Failed to copy tweet.');
            }
        };

        const copyAllTweets = async () => {
            if (!formatsData.value) return;
            const text = formatsData.value.tweets.map(t => t.text).join('\n\n---\n\n');
            try {
                await navigator.clipboard.writeText(text);
                triggerToast('All tweets copied to clipboard!');
            } catch {
                triggerToast('Failed to copy.');
            }
        };

        const copyLinkedin = async () => {
            if (!formatsData.value) return;
            try {
                await navigator.clipboard.writeText(formatsData.value.linkedin);
                triggerToast('LinkedIn post copied!');
            } catch {
                triggerToast('Failed to copy post.');
            }
        };

        const copyYtDesc = async () => {
            if (!formatsData.value) return;
            const text = formatsData.value.description || formatsData.value.ytDesc;
            try {
                await navigator.clipboard.writeText(text);
                triggerToast('YouTube description copied!');
            } catch {
                triggerToast('Failed to copy.');
            }
        };

        const copySummary = async () => {
            if (!formatsData.value) return;
            const text = `TL;DR:\n${formatsData.value.tldr}\n\nKey Takeaways:\n• ${formatsData.value.takeaways.join('\n• ')}`;
            try {
                await navigator.clipboard.writeText(text);
                triggerToast('AI summary contents copied!');
            } catch {
                triggerToast('Failed to copy.');
            }
        };

        const copyTags = async () => {
            if (!formatsData.value || !formatsData.value.tags || formatsData.value.tags.length === 0) return;
            const text = formatsData.value.tags.join(', ');
            try {
                await navigator.clipboard.writeText(text);
                triggerToast('Video tags copied to clipboard!');
            } catch {
                triggerToast('Failed to copy tags.');
            }
        };

        // ── LocalStorage History Management ────────────────────────────────
        const loadHistory = () => {
            try {
                const stored = localStorage.getItem('todoscribe_history');
                if (stored) {
                    history.value = JSON.parse(stored);
                }
            } catch (e) {
                console.error('Failed to load history', e);
            }
        };

        const saveToHistory = (item) => {
            try {
                // Remove duplicates of same URL
                history.value = history.value.filter(h => h.url !== item.url);
                history.value.unshift(item);
                
                // Limit history capacity to 10 entries
                history.value = history.value.slice(0, 10);
                localStorage.setItem('todoscribe_history', JSON.stringify(history.value));
            } catch (e) {
                console.error('Failed to save to history', e);
            }
        };

        const clearHistory = () => {
            history.value = [];
            localStorage.removeItem('todoscribe_history');
            triggerToast('History logs cleared!');
        };

        const loadFromHistory = (item) => {
            videoUrl.value = item.url;
            transcript.value = item.text;
            detectedLanguage.value = item.language;
            metaData.value = item.meta || null;
            activeTab.value = 'transcript';
            generatedOutputText.value = '';
            triggerToast('Loaded transcription from history!');
        };

        // ── Sidebar AI Content Generator logic ──────────────────────────────
        const runGenerate = () => {
            if (!transcript.value) return;
            isGenerating.value = true;
            
            // Mock dynamic templates using the real transcription text, tone, length & prompts
            const tone = activeTone.value;
            const len = activeLength.value;
            const prompt = customPrompt.value.trim();
            const textSummary = formatsData.value ? formatsData.value.tldr : 'Core concepts breakdown.';
            
            setTimeout(() => {
                const type = activeGen.value;
                let output = '';
                
                if (type === 'linkedin') {
                    output = `💼 LINKEDIN POST [Tone: ${tone} | Length: ${len}]\n\nJust completed transcribing this fascinating video. Here is the core strategy you need to know:\n\n• ${formatsData.value.takeaways.slice(0, 3).join('\n• ')}\n\nSystems and leverage beat virality. Don't trade hours for single posts.\n\n#CreatorEconomy #SocialMediaSystems #Leverage`;
                } else if (type === 'article') {
                    output = `✍️ BLOG ARTICLE PROMPT STRUCTURE [Tone: ${tone}]\n\n"Consistency is the currency of the creator economy. Most writers fail because they create reactively rather than building structural assets."\n\nFull blog article has been dynamically generated under the Blog Post tab above!`;
                } else if (type === 'hooks') {
                    output = `🎣 HOOK OPTIONS [Tone: ${tone}]\n\n1. "I spent 3 years trying to grow. Last month, this single system changed everything."\n2. "Stop filming more content. Do this 30-second repurposing trick instead."\n3. "The silent killer of most content channels? No leverage."\n4. "The exact math that turns 50K raw views into consistent recurring revenue."`;
                } else if (type === 'thread') {
                    output = `🐦 THREAD PREVIEW [Tone: ${tone}]\n\n"I spent 3 years posting with no system.\n\nLast month: $10K from content alone.\n\nHere's the exact shift... 🧵"\n\nFull thread has been populated under the Tweets tab above!`;
                } else if (type === 'newsletter') {
                    output = `📧 EMAIL NEWSLETTER DRAFT\n\nSubject: Steal this video breakdown: How to work 5x smarter 🚀\n\nHey reader,\n\nAre you still filming seven different videos a week? If so, you're working much harder than you need to.\n\nHere's the exact content engine system that top creators use to build massive distribution with a fraction of the work...`;
                } else if (type === 'caption') {
                    output = `📸 INSTAGRAM CAPTION\n\nOne video. Seven pieces of content. Zero extra filming. 🎯\n\nThe content engine that changed everything for me 👇\n\nSave this post and I'll send you the full template.\n\n#ContentCreator #SocialMediaTips #CreatorEconomy`;
                } else if (type === 'subjects') {
                    output = `📬 EMAIL SUBJECTS\n\n• "Steal my $10K content system (it's free)"\n• "Why you're working 10x harder than you need to"\n• "One video → 7 posts? Here's how"\n• "The content repurposing method no one talks about"`;
                } else if (type === 'ytdesc') {
                    output = `▶️ YT DESCRIPTION\n\n🚀 Build a $10K/Month Content Engine.\n\nIn this video I reveal the exact system to turn ONE video into 7+ pieces of content across every major platform — automatically.`;
                } else if (type === 'seo') {
                    output = `🔍 SEO META\n\nTitle: How to Build a $10K/Month Content Engine (2025 Guide)\nDescription: Discover the exact system top creators use to turn one video into 7 pieces of content. Step-by-step repurposing framework included.\nKeywords: content engine, repurposing strategy, creator monetization`;
                } else if (type === 'titles') {
                    output = `🏷️ VIDEO TITLE OPTIONS\n\n1. "How I Build a $10K/Month Content Engine (Full System)"\n2. "One Video → 7 Pieces of Content | The Creator's Secret"\n3. "Stop Creating More Content. Do This Instead."\n4. "The Repurposing System That 10x'd My Reach"`;
                } else if (type === 'podcast') {
                    output = `🎙️ SHOW NOTES\n\nEpisode: How to Build a Content Engine\nDuration: 18:42 | Guest: Alex Hormozi\n\nKey Topics: One-Video Rule, AI transcription, multi-platform distribution, creator revenue math`;
                } else if (type === 'outreach') {
                    output = `🤝 COLD OUTREACH EMAIL\n\nSubject: Quick idea for content\n\nHey there, I just watched your latest video — the insight was genuinely useful.\n\nI help creators turn single videos into 7+ pieces of content automatically. Would a 10-min call make sense?`;
                }
                
                // Add custom prompt input if present
                if (prompt) {
                    output = `🎯 CUSTOM AI GENERATION [Prompt: "${prompt}"]\n\n${output}`;
                }

                generatedOutputText.value = output;
                generatedOutputLabel.value = `${activeGen.value.toUpperCase()} (${tone})`;
                isGenerating.value = false;
                triggerToast(`${activeGen.value.toUpperCase()} generated!`);
            }, 1200);
        };

        // ── Methods ────────────────────────────────────────────────────────
        const startProgressSimulation = () => {
            progress.value = 5;
            progressLabel.value = 'Processing';
            loadingMessage.value = 'Connecting to YouTube...';
            
            const steps = [
                { msg: 'Connecting to platform...', pct: 5 },
                { msg: 'Fetching video metadata...', pct: 18 },
                { msg: 'Downloading audio stream...', pct: 34 },
                { msg: 'Running AI transcription (Whisper)...', pct: 52 },
                { msg: 'Detecting speakers and tracks...', pct: 68 },
                { msg: 'Generating content formats...', pct: 82 },
                { msg: 'Polishing content workspace...', pct: 94 },
            ];

            stepIndex = 0;
            stepInterval = setInterval(() => {
                if (stepIndex < steps.length) {
                    const step = steps[stepIndex];
                    progress.value = step.pct;
                    loadingMessage.value = step.msg;
                    stepIndex++;
                } else {
                    // Slowly creeping to wait for API
                    if (progress.value < 99) {
                        progress.value = Math.min(progress.value + 0.2, 99);
                    }
                }
            }, 1800);
        };

        const stopProgressSimulation = () => {
            if (stepInterval) {
                clearInterval(stepInterval);
                stepInterval = null;
            }
        };

        const handleTranscribe = async () => {
            const url = videoUrl.value.trim();
            if (!url) return;

            error.value = null;
            transcript.value = '';
            detectedLanguage.value = '';
            generatedOutputText.value = '';
            activeTab.value = 'transcript';
            isProcessing.value = true;

            startProgressSimulation();

            try {
                const response = await axios.post('/transcribe', {
                    video_url: url,
                }, {
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                    timeout: 600000, // 10 mins
                });

                stopProgressSimulation();
                progress.value = 100;

                await new Promise(r => setTimeout(r, 600));

                if (response.data.success === false) {
                    error.value = response.data.message || 'Transcription failed. Please try again.';
                } else {
                    transcript.value = response.data.text || '';
                    detectedLanguage.value = response.data.language || 'English';
                    metaData.value = response.data.meta || null;
                    
                    // Save to persistent local storage history
                    const wordCountVal = transcript.value.trim().split(/\s+/).filter(Boolean).length;
                    const histItem = {
                        id: Date.now(),
                        url,
                        title: formatsData.value ? formatsData.value.videoTitle : 'Video Workspace',
                        text: transcript.value,
                        language: detectedLanguage.value,
                        wordCount: wordCountVal,
                        platform: formatsData.value ? formatsData.value.platform : 'YouTube',
                        thumbBg: formatsData.value && formatsData.value.platform === 'TikTok' ? '#001a10' : formatsData.value && formatsData.value.platform === 'Instagram' ? '#1a001a' : '#1a0000',
                        meta: response.data.meta || null
                    };
                    saveToHistory(histItem);
                }
            } catch (err) {
                stopProgressSimulation();
                const msg = err.response?.data?.message
                    || err.response?.data?.error
                    || (err.code === 'ECONNABORTED' ? 'Request timed out. Whisper took too long.' : 'Connection error. Please try again.');
                error.value = msg;
            } finally {
                isProcessing.value = false;
            }
        };

        const reset = () => {
            videoUrl.value = '';
            transcript.value = '';
            detectedLanguage.value = '';
            error.value = null;
            progress.value = 0;
            copied.value = false;
            generatedOutputText.value = '';
            stopProgressSimulation();
        };

        const copyGenerated = async () => {
            if (!generatedOutputText.value) return;
            try {
                await navigator.clipboard.writeText(generatedOutputText.value);
                triggerToast('Generated content copied!');
            } catch {
                triggerToast('Failed to copy.');
            }
        };

        // Toggle body scrollbar depending on whether workspace takeover is active
        watch(
            [isProcessing, transcript],
            ([newProcessing, newTranscript]) => {
                if (newProcessing || newTranscript) {
                    document.body.style.overflow = 'hidden';
                } else {
                    document.body.style.overflow = '';
                }
            },
            { immediate: true }
        );

        onUnmounted(() => {
            document.body.style.overflow = '';
        });

        onMounted(() => {
            loadHistory();
        });

        return {
            videoUrl, isProcessing, transcript, detectedLanguage,
            error, copied, progress, progressLabel, loadingMessage, metaData,
            activeTab, activeGen, activeTone, activeLength, outputLang,
            customPrompt, generatedOutputText, generatedOutputLabel,
            isGenerating, showToast, toastMessage, history,
            showTimestamps, showSpeakers, searchText,
            wordCount, workspaceTitle, filteredTranscriptLines, formatsData, watchPlatformText,
            handleTranscribe, reset, triggerToast, runGenerate,
            copyTabContent, downloadTabTxt, copyVideoLink, copySingleTweet,
            copyAllTweets, copyLinkedin, copyYtDesc, copySummary, copyTags,
            clearHistory, loadFromHistory, copyGenerated,
        };
    },
};
</script>

<style scoped>
.workspace-root {
  --green: #00e5a0;
  --green-dim: #00c488;
  --green-xdim: #009e70;
  --green-bg: rgba(0, 229, 160, 0.08);
  --green-border: rgba(0, 229, 160, 0.2);
  --dark: #070d18;
  --dark2: #0b1221;
  --dark3: #101926;
  --dark4: #162030;
  --dark5: #0d1620;
  --border: #1a2d44;
  --border2: #203650;
  --muted: #42607e;
  --text: #9ab8d8;
  --text2: #6b90b0;
  --font: Arial, 'Helvetica Neue', Helvetica, sans-serif;
  --radius: 10px;
  --radius-lg: 14px;
  --radius-xl: 18px;
}

/* ── LAYOUT App Shell ── */
#app-shell {
  display: flex;
  height: calc(100vh - 58px);
  overflow: hidden;
}

#left-sidebar {
  width: 272px;
  flex-shrink: 0;
  background: var(--dark2);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

#main-area {
  flex: 1;
  overflow-y: auto;
  padding: 20px 22px;
}

/* ── SIDEBAR SECTIONS ── */
.sb-header {
  padding: 14px 16px 10px;
  border-bottom: 1px solid var(--border);
  background: var(--dark3);
}

.sb-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
}

.sb-section {
  margin-bottom: 20px;
}

.sb-section-title {
  font-size: 10px;
  font-weight: 700;
  color: var(--muted);
  letter-spacing: .09em;
  text-transform: uppercase;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.sb-section-title::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border);
}

/* ── AI GEN BUTTONS ── */
.gen-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.gen-btn {
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 9px 8px;
  cursor: pointer;
  transition: all .18s;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  color: var(--text2);
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.2;
}

.gen-btn:hover {
  border-color: var(--green-border);
  color: var(--green);
  background: var(--green-bg);
  transform: translateY(-1px);
}

.gen-btn.active-gen {
  border-color: var(--green);
  color: var(--green);
  background: var(--green-bg);
}

.gen-btn .gicon {
  font-size: 18px;
  line-height: 1;
}

/* ── TONE PILLS ── */
.tone-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.tone-pill {
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: 100px;
  padding: 5px 11px;
  cursor: pointer;
  transition: all .15s;
  font-size: 11px;
  font-weight: 600;
  color: var(--text2);
}

.tone-pill:hover {
  border-color: var(--green-border);
  color: var(--green);
}

.tone-pill.active {
  background: var(--green-bg);
  border-color: var(--green);
  color: var(--green);
}

/* ── LENGTH SELECTOR ── */
.len-opt {
  display: flex;
  gap: 4px;
}

.len-btn {
  flex: 1;
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: 7px;
  padding: 6px 4px;
  cursor: pointer;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--text2);
  transition: all .15s;
}

.len-btn:hover {
  border-color: var(--green-border);
  color: var(--green);
}

.len-btn.active {
  background: var(--green-bg);
  border-color: var(--green);
  color: var(--green);
}

/* ── CUSTOM PROMPT ── */
.prompt-area {
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 10px 12px;
  width: 100%;
  color: var(--text);
  font-size: 12.5px;
  resize: none;
  outline: none;
  transition: border .2s;
  line-height: 1.55;
}

.prompt-area:focus {
  border-color: var(--green);
}

.prompt-area::placeholder {
  color: var(--muted);
}

/* ── EXPORT INTEGRATIONS ── */
.export-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  margin-bottom: 6px;
  cursor: pointer;
  transition: all .15s;
}

.export-row:hover {
  border-color: var(--green-border);
  background: rgba(0, 229, 160, .04);
}

.export-row:hover .exp-arrow {
  color: var(--green);
}

.exp-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
}

.exp-arrow {
  color: var(--muted);
  font-size: 13px;
  transition: color .15s;
}

.exp-logo {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}

/* ── HISTORY ITEMS ── */
.hist-item {
  display: flex;
  gap: 9px;
  padding: 8px 10px;
  border-radius: var(--radius);
  cursor: pointer;
  transition: background .15s;
  border: 1px solid transparent;
}

.hist-item:hover {
  background: var(--dark3);
  border-color: var(--border);
}

.hist-item.active-gen {
  border-color: var(--green-border);
  background: var(--green-bg);
}

.hist-thumb {
  width: 42px;
  height: 26px;
  border-radius: 5px;
  background: var(--dark4);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: var(--muted);
  overflow: hidden;
}

.hist-title {
  font-size: 11.5px;
  color: var(--text2);
  line-height: 1.4;
  font-weight: 500;
}

.hist-meta {
  font-size: 10px;
  color: var(--muted);
  margin-top: 2px;
}

/* ── GENERATE BTN ── */
.generate-btn {
  width: 100%;
  background: var(--green);
  color: #07111e;
  font-weight: 700;
  font-size: 13.5px;
  padding: 12px;
  border-radius: var(--radius);
  border: none;
  cursor: pointer;
  transition: all .2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  letter-spacing: .01em;
}

.generate-btn:hover {
  background: var(--green-dim);
  transform: translateY(-1px);
}

.generate-btn:active {
  transform: scale(.98);
}

.generate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── MAIN CONTENT ── */
.video-card {
  background: var(--dark2);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 16px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.video-thumb {
  width: 200px;
  height: 112px;
  border-radius: 8px;
  flex-shrink: 0;
  background: var(--dark4);
  overflow: hidden;
  position: relative;
}

.video-thumb-link-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  text-decoration: none;
  color: inherit;
}

.play-over {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, .2);
  transition: background-color 0.25s cubic-bezier(0, 0, 0.2, 1);
}

.video-thumb:hover .play-over {
  background: rgba(0, 0, 0, .45);
}

.yt-play-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s cubic-bezier(0, 0, 0.2, 1);
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.45));
}

.yt-play-bg {
  fill: #ff0000;
  transition: fill 0.25s cubic-bezier(0, 0, 0.2, 1);
}

.video-thumb:hover .yt-play-btn {
  transform: scale(1.05);
}

.video-thumb:hover .yt-play-bg {
  fill: #cc0000;
}

.yt-watch-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(39, 39, 39, 0.95);
  color: #fff;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: transform 0.25s cubic-bezier(0, 0, 0.2, 1), opacity 0.25s cubic-bezier(0, 0, 0.2, 1);
  transform: translateY(100%);
  opacity: 0;
}

.video-thumb:hover .yt-watch-bar {
  transform: translateY(0);
  opacity: 1;
}

.meta-chip {
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: 7px;
  padding: 4.5px 11px;
  font-size: 12.5px;
  color: var(--text2);
  display: inline-flex;
  align-items: center;
  gap: 6.5px;
}

/* ── STATS ROW ── */
.stats-row {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.stat-box {
  background: var(--dark2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 10px 14px;
  flex: 1;
  text-align: center;
}

.stat-num {
  font-size: 18px;
  font-weight: 700;
  color: var(--green);
}

.stat-lbl {
  font-size: 10px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: .06em;
  margin-top: 2px;
}

/* ── TABS ── */
.tab-wrap {
  display: flex;
  gap: 2px;
  background: var(--dark2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 4px;
  overflow-x: auto;
}

.tab-btn {
  padding: 7px 13px;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  background: transparent;
  color: var(--text2);
  font-weight: 600;
  font-size: 12px;
  transition: all .18s;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.tab-btn:hover {
  color: var(--text);
  background: rgba(255, 255, 255, .03);
}

.tab-btn.active {
  background: var(--dark4);
  color: white;
  border: 1px solid var(--border2);
}

.panel {
  display: none;
  opacity: 0;
}

.panel.active {
  display: block;
  animation: fadeInPanel 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes fadeInPanel {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── PANEL CONTAINER ── */
.panel-wrap {
  background: var(--dark2);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.panel-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px solid var(--border);
  background: var(--dark3);
}

.panel-body {
  padding: 20px;
  height: 420px;
  max-height: 420px;
  overflow-y: auto;
}

/* ── BTNS ── */
.btn-sm {
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: 7px;
  padding: 7px 13px;
  font-size: 12px;
  color: var(--text2);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all .15s;
  font-weight: 600;
}

.btn-sm:hover {
  border-color: var(--green);
  color: var(--green);
}

.btn-sm:active {
  transform: scale(.97);
}

.btn-green {
  background: var(--green);
  color: #07111e;
  border: none;
  font-weight: 700;
}

.btn-green:hover {
  background: var(--green-dim);
  border: none;
  color: #07111e;
}

/* ── TRANSCRIPT Verbatim lines ── */
.tr-line {
  display: grid;
  grid-template-columns: 54px 1fr;
  gap: 10px;
  padding: 9px 8px;
  border-bottom: 1px solid rgba(26, 45, 68, .5);
  cursor: pointer;
  transition: all .12s;
  border-radius: 6px;
}

.tr-line:hover {
  background: rgba(0, 229, 160, .03);
  border-bottom-color: transparent;
}

.ts-stamp {
  font-size: 11px;
  color: var(--green);
  font-weight: 600;
  letter-spacing: .02em;
  padding-top: 1px;
}

.spk {
  font-size: 10px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: .07em;
  margin-bottom: 2px;
}

.tr-text {
  font-size: 13.5px;
  color: var(--text);
  line-height: 1.65;
}

/* ── BLOG POST markup scoped ── */
.blog-wrap h1 {
  font-size: 22px;
  font-weight: 700;
  color: white;
  line-height: 1.28;
  margin-bottom: 14px;
  letter-spacing: -.3px;
}

.blog-wrap h2 {
  font-size: 16px;
  font-weight: 700;
  color: white;
  margin: 24px 0 10px;
}

.blog-wrap p {
  font-size: 13.5px;
  color: var(--text);
  line-height: 1.8;
  margin-bottom: 14px;
}

/* ── TWEETS sequential cards ── */
.tweet-card {
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 16px;
  margin-bottom: 10px;
  position: relative;
  transition: border-color .15s;
}

.tweet-card:hover {
  border-color: var(--border2);
}

.tweet-num {
  font-size: 10px;
  font-weight: 700;
  color: var(--muted);
  letter-spacing: .06em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.tweet-text {
  font-size: 13.5px;
  color: var(--text);
  line-height: 1.65;
  white-space: pre-line;
}

.tweet-chars {
  font-size: 10.5px;
  color: var(--muted);
}

.tweet-chars b {
  color: var(--green);
}

.copy-tw {
  position: absolute;
  top: 12px;
  right: 12px;
  background: var(--dark2);
  border: 1px solid var(--border);
  border-radius: 5px;
  padding: 4px 9px;
  font-size: 10.5px;
  color: var(--muted);
  cursor: pointer;
  transition: all .15s;
}

.copy-tw:hover {
  color: var(--green);
  border-color: var(--green);
}

/* ── NEWSLETTER box layout ── */
.email-shell {
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.em-head {
  background: var(--dark4);
  border-bottom: 1px solid var(--border);
  padding: 14px 20px;
}

.em-subj {
  font-size: 15px;
  font-weight: 700;
  color: white;
  margin-top: 4px;
}

.em-body {
  padding: 22px 24px;
}

.em-div {
  height: 1px;
  background: var(--border);
  margin: 16px 0;
}

.em-cta {
  background: var(--green);
  color: #07111e;
  font-weight: 700;
  text-align: center;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  display: block;
}

/* ── LINKEDIN ── */
.li-card {
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 20px;
}

.li-av {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 229, 160, .15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: var(--green);
  font-size: 15px;
  flex-shrink: 0;
}

.li-text {
  font-size: 13.5px;
  color: var(--text);
  line-height: 1.78;
  white-space: pre-line;
}

/* ── YT DESCRIPTION Chapters ── */
.yt-lbl {
  font-size: 10px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: .07em;
  margin-bottom: 7px;
}

.yt-val {
  font-size: 13px;
  color: var(--text);
  line-height: 1.75;
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 12px 14px;
  margin-bottom: 16px;
  overflow-x: auto;
}

/* ── SUMMARY TLDR Insights ── */
.sum-box {
  background: var(--dark3);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 18px;
  margin-bottom: 14px;
}

.kp-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid rgba(26, 45, 68, .4);
}

.kp-row:last-child {
  border-bottom: none;
}

.kpdot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--green);
  flex-shrink: 0;
  margin-top: 6px;
}

/* ── LOADING LAYOUT SPINNER ── */
.spinner {
  width: 44px;
  height: 44px;
  border: 2px solid var(--dark3);
  border-top-color: var(--green);
  border-radius: 50%;
  animation: spin .75s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.prog-track {
  width: 360px;
  height: 3px;
  background: var(--dark3);
  border-radius: 100px;
  overflow: hidden;
}

.prog-fill {
  height: 100%;
  background: var(--green);
  border-radius: 100px;
  width: 0%;
  transition: width .4s ease;
}

/* ── AI GENERATING ANIMATION ── */
@keyframes pulse {
  0%, 100% { opacity: .5; }
  50% { opacity: 1; }
}

.ai-pulse {
  animation: pulse 1.2s ease-in-out infinite;
}

/* ── CLIENT-SIDE TOAST NOTIFICATIONS ── */
#toast {
  position: fixed;
  bottom: 28px;
  right: 28px;
  background: var(--dark4);
  border: 1px solid var(--green);
  border-radius: var(--radius);
  padding: 11px 18px;
  font-size: 13px;
  color: var(--green);
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 7px;
  z-index: 99999;
  opacity: 0;
  transform: translateY(10px);
  transition: all .28s;
  pointer-events: none;
}

#toast.show {
  opacity: 1;
  transform: translateY(0);
}

.usage-bar {
  height: 4px;
  background: var(--dark4);
  border-radius: 100px;
  overflow: hidden;
  margin-top: 6px;
}

.usage-fill {
  height: 100%;
  background: var(--green);
  border-radius: 100px;
  transition: width .3s;
}

/* ── DEFAULT HOMEPAGE URL GLOW ── */
.glow {
  box-shadow: 0 0 40px rgba(0, 229, 160, 0.12);
}

.url-input {
  background: transparent !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  color: var(--text) !important;
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  flex: 1;
  padding: 10px 4px;
  width: 100%;
}

.url-input:focus,
.url-input:active,
.url-input:hover {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
}

.url-input:-webkit-autofill,
.url-input:-webkit-autofill:hover, 
.url-input:-webkit-autofill:focus,
.url-input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px var(--dark2) inset !important;
  -webkit-text-fill-color: var(--text) !important;
  transition: background-color 5000000s ease-in-out 0s;
}

.url-input::placeholder {
  color: var(--muted);
}

.btn-primary {
  background: var(--green);
  color: #080c14;
  font-family: 'Syne', sans-serif;
  font-weight: 700;
  padding: 12px 28px;
  border-radius: 8px;
  cursor: pointer;
  transition: all .2s;
  border: none;
  font-size: 15px;
  white-space: nowrap;
}

.btn-primary:hover {
  background: var(--green-dim);
  transform: translateY(-1px);
}

/* Remove all click focus rings */
*:focus,
button:focus,
a:focus,
input:focus,
textarea:focus,
select:focus,
.btn-sm:focus,
.btn-primary:focus,
.gen-btn:focus,
.tone-pill:focus,
.len-btn:focus,
.tab-btn:focus {
  outline: none !important;
  box-shadow: none !important;
}
</style>
